export interface TimeOffCalendarFetchRange {
  from: string;
  to: string;
}

export interface TimeOffCalendarDateRange {
  start: Date;
  end: Date;
}

/** Matches `firstDayOfWeek` on the Time Off Scheduler (Sunday). */
export const SCHEDULER_FIRST_DAY_OF_WEEK = 0;

/**
 * DevExtreme month view always renders a fixed number of week rows. Internally it pads short months
 * up to this count (`Math.max(weekCount, 6)`), so the visible grid can spill a full extra week into
 * the next month. We mirror that here so the fetch/agenda range covers the trailing row.
 */
export const SCHEDULER_MONTH_WEEK_ROWS = 6;

function localMidnight(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfWeek(date: Date, firstDayOfWeek = SCHEDULER_FIRST_DAY_OF_WEEK): Date {
  const d = localMidnight(date);
  const day = d.getDay();
  const diff = (day - firstDayOfWeek + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function endOfWeek(date: Date, firstDayOfWeek = SCHEDULER_FIRST_DAY_OF_WEEK): Date {
  return endOfDay(addDays(startOfWeek(date, firstDayOfWeek), 6));
}

function startOfWorkWeek(date: Date): Date {
  const d = localMidnight(date);
  const dow = d.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  return addDays(d, mondayOffset);
}

function endOfWorkWeek(date: Date): Date {
  return endOfDay(addDays(startOfWorkWeek(date), 4));
}

function startOfMonth(date: Date): Date {
  return localMidnight(new Date(date.getFullYear(), date.getMonth(), 1));
}

/**
 * Visible month grid in DevExtreme month view: a fixed {@link SCHEDULER_MONTH_WEEK_ROWS}-week block
 * starting on the week that contains the 1st of the month. This includes the trailing spillover row
 * DevExtreme pads in for short months (e.g. viewing June can show the first full week of July).
 */
export function computeCalendarMonthGridRange(
  currentDate: Date,
  firstDayOfWeek = SCHEDULER_FIRST_DAY_OF_WEEK
): TimeOffCalendarDateRange {
  const gridStart = startOfWeek(startOfMonth(currentDate), firstDayOfWeek);
  const gridEnd = endOfDay(addDays(gridStart, SCHEDULER_MONTH_WEEK_ROWS * 7 - 1));
  return { start: gridStart, end: gridEnd };
}

export function computeCalendarFetchRange(currentView: string, currentDate: Date): TimeOffCalendarFetchRange {
  const anchor = new Date(currentDate);
  let rangeStart: Date;
  let rangeEnd: Date;

  switch (currentView) {
    case 'day':
      rangeStart = localMidnight(addDays(anchor, -1));
      rangeEnd = endOfDay(addDays(anchor, 1));
      break;
    case 'week':
      rangeStart = addDays(startOfWeek(anchor), -3);
      rangeEnd = endOfDay(addDays(endOfWeek(anchor), 3));
      break;
    case 'workWeek':
      rangeStart = addDays(startOfWorkWeek(anchor), -3);
      rangeEnd = endOfDay(addDays(endOfWorkWeek(anchor), 3));
      break;
    case 'month':
    default: {
      const grid = computeCalendarMonthGridRange(anchor);
      rangeStart = addDays(grid.start, -1);
      rangeEnd = endOfDay(addDays(grid.end, 1));
      break;
    }
  }

  return {
    from: rangeStart.toISOString(),
    to: rangeEnd.toISOString()
  };
}
